import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import api, { API_ENDPOINTS } from '../config/api';
import { 
  ArrowLeft, 
  DollarSign, 
  Clock, 
  Tag, 
  FileText,
  Upload,
  Plus,
  X,
  Image,
  Video,
  Sparkles
} from 'lucide-react';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import ProfessionalRichTextEditor from '../components/ProfessionalRichTextEditor';
import PostQualityScore from '../components/PostQualityScore';
import AiWriteModal from '../components/AiWriteModal';
import { GIG_CATEGORIES, formatCategoryName, formatSubCategoryName } from '../constants/categories';

const CreateGigPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [includes, setIncludes] = useState(['']);
  const [skills, setSkills] = useState(['']);
  const [portfolio, setPortfolio] = useState([{ description: '', imageFile: null, imagePreview: null, imageUrl: '', videoUrl: '' }]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubCategory, setSelectedSubCategory] = useState('');
  const [qualityPasses, setQualityPasses] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);
  const [location, setLocation] = useState({
    remote: true,
    city: '',
    country: ''
  });
  
  // Basic/Premium offers state
  const [basicOffer, setBasicOffer] = useState({
    price: '',
    deliveryValue: '',
    deliveryUnit: 'days',
    pros: [''],
    cons: ['']
  });
  const [premiumOffer, setPremiumOffer] = useState({
    price: '',
    deliveryValue: '',
    deliveryUnit: 'days',
    pros: [''],
    cons: ['']
  });

  // Create gig mutation with FormData for file upload
  const createGigMutation = useMutation(
    (formData) => {
      return api.post(API_ENDPOINTS.GIGS.CREATE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    {
      onSuccess: () => {
        toast.success('Gig created successfully!');
        queryClient.invalidateQueries(['gigs']);
        queryClient.invalidateQueries(['myGigs']);
        navigate('/dashboard?tab=gigs');
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors && Array.isArray(errorData.errors)) {
          // Handle validation errors
          errorData.errors.forEach(err => {
            toast.error(err.msg || err.message || 'Validation error');
          });
        } else if (errorData?.error) {
          toast.error(errorData.error);
        } else {
          toast.error('Failed to create gig. Please check all required fields are filled correctly.');
        }
      }
    }
  );

  const { register, handleSubmit, formState: { errors }, watch, control, setValue } = useForm();

  const addInclude = () => {
    setIncludes([...includes, '']);
  };

  const removeInclude = (index) => {
    if (includes.length > 1) {
      setIncludes(includes.filter((_, i) => i !== index));
    }
  };

  const updateInclude = (index, value) => {
    const newIncludes = [...includes];
    newIncludes[index] = value;
    setIncludes(newIncludes);
  };

  const addSkill = () => {
    setSkills([...skills, '']);
  };

  const removeSkill = (index) => {
    if (skills.length > 1) {
      setSkills(skills.filter((_, i) => i !== index));
    }
  };

  const updateSkill = (index, value) => {
    const newSkills = [...skills];
    newSkills[index] = value;
    setSkills(newSkills);
  };

  const addPortfolioItem = () => {
    setPortfolio([...portfolio, { description: '', imageFile: null, imagePreview: null, imageUrl: '', videoUrl: '' }]);
  };

  const removePortfolioItem = (index) => {
    if (portfolio.length > 1) {
      setPortfolio(portfolio.filter((_, i) => i !== index));
    }
  };

  const updatePortfolioItem = (index, field, value) => {
    const newPortfolio = [...portfolio];
    newPortfolio[index][field] = value;
    setPortfolio(newPortfolio);
  };

  const handlePortfolioImageChange = (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const newPortfolio = [...portfolio];
        newPortfolio[index].imageFile = file;
        newPortfolio[index].imagePreview = reader.result;
        newPortfolio[index].imageUrl = ''; // Clear URL if file is selected
        setPortfolio(newPortfolio);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const addPro = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, pros: [...prev.pros, ''] }));
    } else {
      setPremiumOffer(prev => ({ ...prev, pros: [...prev.pros, ''] }));
    }
  };

  const removePro = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, pros: prev.pros.filter((_, i) => i !== index) }));
    } else {
      setPremiumOffer(prev => ({ ...prev, pros: prev.pros.filter((_, i) => i !== index) }));
    }
  };

  const updatePro = (offerType, index, value) => {
    if (offerType === 'basic') {
      const newPros = [...basicOffer.pros];
      newPros[index] = value;
      setBasicOffer(prev => ({ ...prev, pros: newPros }));
    } else {
      const newPros = [...premiumOffer.pros];
      newPros[index] = value;
      setPremiumOffer(prev => ({ ...prev, pros: newPros }));
    }
  };

  const addCon = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, cons: [...prev.cons, ''] }));
    } else {
      setPremiumOffer(prev => ({ ...prev, cons: [...prev.cons, ''] }));
    }
  };

  const removeCon = (offerType, index) => {
    if (offerType === 'basic') {
      setBasicOffer(prev => ({ ...prev, cons: prev.cons.filter((_, i) => i !== index) }));
    } else {
      setPremiumOffer(prev => ({ ...prev, cons: prev.cons.filter((_, i) => i !== index) }));
    }
  };

  const updateCon = (offerType, index, value) => {
    if (offerType === 'basic') {
      const newCons = [...basicOffer.cons];
      newCons[index] = value;
      setBasicOffer(prev => ({ ...prev, cons: newCons }));
    } else {
      const newCons = [...premiumOffer.cons];
      newCons[index] = value;
      setPremiumOffer(prev => ({ ...prev, cons: newCons }));
    }
  };

  const onSubmit = (data) => {
    console.log('Form data:', data);
    console.log('Selected category:', selectedCategory);
    console.log('Category from form:', data.category);
    
    // Validate image is selected (mandatory for gigs)
    if (!selectedImage) {
      toast.error('Please upload a gig image');
      return;
    }

    // Validate category is selected
    if (!data.category || data.category === '') {
      toast.error('Please select a category');
      return;
    }

    // Validate description has content
    if (!data.description || data.description.trim() === '' || data.description.replace(/<[^>]*>/g, '').trim() === '') {
      toast.error('Please provide a description with actual content');
      return;
    }

    if (!qualityPasses) {
      toast.error('Score must be above 80% to post this gig');
      return;
    }

    // Validate basic and premium offers
    if (!basicOffer.price || !premiumOffer.price) {
      toast.error('Both basic and premium offers are required with prices');
      return;
    }

    // Build pricing object with basic/premium offers
    const pricing = {
      basic: {
        price: parseFloat(basicOffer.price),
        deliveryTime: {
          value: parseInt(basicOffer.deliveryValue) || 1,
          unit: basicOffer.deliveryUnit
        },
        pros: basicOffer.pros.filter(p => p.trim() !== ''),
        cons: basicOffer.cons.filter(c => c.trim() !== '')
      },
      premium: {
        price: parseFloat(premiumOffer.price),
        deliveryTime: {
          value: parseInt(premiumOffer.deliveryValue) || 1,
          unit: premiumOffer.deliveryUnit
        },
        pros: premiumOffer.pros.filter(p => p.trim() !== ''),
        cons: premiumOffer.cons.filter(c => c.trim() !== '')
      }
    };

    // Create FormData for file upload
    const formData = new FormData();
    
    // Append all text fields
    formData.append('title', data.title);
    formData.append('description', data.description);
    formData.append('category', data.category);
    if (data.subCategory) {
      formData.append('subCategory', data.subCategory);
    }
    formData.append('type', data.type);
    formData.append('pricing', JSON.stringify(pricing));
    formData.append('location', JSON.stringify(location));
    
    // Append arrays
    formData.append('includes', JSON.stringify(includes.filter(inc => inc.trim() !== '')));
    formData.append('skills', JSON.stringify(skills.filter(skill => skill.trim() !== '')));
    
    // Handle portfolio - build array and upload images separately
    const portfolioItems = portfolio.filter(item => item.description.trim() !== '' || item.imageFile || item.imageUrl);
    const portfolioData = portfolioItems.map((item, index) => {
      const portfolioItem = {
        description: item.description,
        videoUrl: item.videoUrl || '',
        imageIndex: item.imageFile ? index : null
      };
      if (item.imageUrl && !item.imageFile) {
        portfolioItem.imageUrl = item.imageUrl;
      }
      return portfolioItem;
    });
    
    // Append portfolio as JSON string
    formData.append('portfolio', JSON.stringify(portfolioData));
    
    // Append portfolio images as separate files
    portfolioItems.forEach((item, index) => {
      if (item.imageFile) {
        formData.append(`portfolioImage_${index}`, item.imageFile);
      }
    });

    // Append image file
    formData.append('image', selectedImage);

    // Debug: Log FormData contents
    console.log('FormData contents:');
    for (let [key, value] of formData.entries()) {
      if (key === 'image') {
        console.log(key, ':', value.name, value.type, value.size);
      } else {
        console.log(key, ':', value);
      }
    }

    createGigMutation.mutate(formData);
  };

  const gigTypes = ['professional', 'labour'];

  const deliveryUnits = ['hours', 'days', 'weeks'];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <button 
          onClick={() => navigate(-1)}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-accent mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>
        
        <h1 className="text-3xl font-heading font-bold text-heading mb-2">
          Create a New Gig
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Showcase your skills and offer your services to clients.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Basic Information
              </h2>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gig Title *
                  </label>
                  <input
                    {...register('title', { required: 'Gig title is required' })}
                    type="text"
                    className="w-full input-field"
                    placeholder="e.g., I will design a modern website for you"
                  />
                  {errors.title && (
                    <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Gig Description *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowAiModal(true)}
                      className="inline-flex items-center gap-1.5 text-sm text-accent hover:opacity-80 font-medium"
                    >
                      <Sparkles className="w-4 h-4" />
                      Use AI to write
                    </button>
                  </div>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: 'Gig description is required' }}
                    render={({ field }) => (
                      <ProfessionalRichTextEditor
                        value={field.value || ''}
                        onChange={field.onChange}
                        contentKey={descriptionEditorKey}
                        placeholder="Describe what you offer, your experience, and what makes you unique. You can format text, add headings, links, images, videos, and tables..."
                      />
                    )}
                  />
                  {errors.description && (
                    <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Category *
                    </label>
                    <Controller
                      name="category"
                      control={control}
                      rules={{ required: 'Category is required' }}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full input-field"
                          onChange={(e) => {
                            field.onChange(e);
                            setSelectedCategory(e.target.value);
                            setSelectedSubCategory('');
                            setValue('subCategory', ''); // Reset sub-category when category changes
                          }}
                        >
                          <option value="">Select a category</option>
                          {Object.entries(GIG_CATEGORIES).map(([key, category]) => (
                            <option key={key} value={key}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.category && (
                      <p className="text-red-500 text-sm mt-1">{errors.category.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sub-Category
                    </label>
                    <Controller
                      name="subCategory"
                      control={control}
                      render={({ field }) => (
                        <select
                          {...field}
                          className="w-full input-field"
                          disabled={!selectedCategory || !watch('category')}
                          onChange={(e) => {
                            field.onChange(e);
                            setSelectedSubCategory(e.target.value);
                          }}
                        >
                          <option value="">Select a sub-category (optional)</option>
                          {selectedCategory && GIG_CATEGORIES[selectedCategory]?.subCategories.map(subCat => (
                            <option key={subCat} value={subCat}>
                              {formatSubCategoryName(subCat)}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Gig Type *
                    </label>
                    <select
                      {...register('type', { required: 'Gig type is required' })}
                      className="w-full input-field"
                    >
                      <option value="">Select gig type</option>
                      {gigTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                    {errors.type && (
                      <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                    )}
                  </div>
                </div>

                {/* Location Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Location
                  </label>
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={location.remote}
                        onChange={(e) => setLocation(prev => ({ ...prev, remote: e.target.checked }))}
                        className="rounded border-gray-300 text-accent focus:ring-accent"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Remote work available</span>
                    </label>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <input
                          type="text"
                          placeholder="City (optional)"
                          value={location.city}
                          onChange={(e) => setLocation(prev => ({ ...prev, city: e.target.value }))}
                          className="w-full input-field"
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Country (optional)"
                          value={location.country}
                          onChange={(e) => setLocation(prev => ({ ...prev, country: e.target.value }))}
                          className="w-full input-field"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Gig Image Upload - Mandatory */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gig Image * (Required)
                  </label>
                  <div className="space-y-2">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark"
                      required
                    />
                    {imagePreview && (
                      <div className="mt-2">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                        />
                      </div>
                    )}
                    {!selectedImage && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        Upload an image to showcase your gig (Max 5MB)
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Basic Offer */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Basic Offer *
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price ($) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={basicOffer.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          setBasicOffer(prev => ({ ...prev, price: value }));
                        }
                      }}
                      className="w-full input-field"
                      placeholder="100"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Delivery Time *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={basicOffer.deliveryValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          setBasicOffer(prev => ({ ...prev, deliveryValue: value }));
                        }
                      }}
                      className="w-full input-field"
                      placeholder="3"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Unit *
                    </label>
                    <select
                      value={basicOffer.deliveryUnit}
                      onChange={(e) => setBasicOffer(prev => ({ ...prev, deliveryUnit: e.target.value }))}
                      className="w-full input-field"
                      required
                    >
                      {deliveryUnits.map(unit => (
                        <option key={unit} value={unit}>
                          {unit.charAt(0).toUpperCase() + unit.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pros
                    </label>
                    <div className="space-y-2">
                      {basicOffer.pros.map((pro, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={pro}
                            onChange={(e) => updatePro('basic', index, e.target.value)}
                            className="flex-1 input-field"
                            placeholder="Enter a pro..."
                          />
                          {basicOffer.pros.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePro('basic', index)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addPro('basic')}
                        className="flex items-center text-accent hover:text-accent/80 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Pro
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cons
                    </label>
                    <div className="space-y-2">
                      {basicOffer.cons.map((con, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={con}
                            onChange={(e) => updateCon('basic', index, e.target.value)}
                            className="flex-1 input-field"
                            placeholder="Enter a con..."
                          />
                          {basicOffer.cons.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCon('basic', index)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addCon('basic')}
                        className="flex items-center text-accent hover:text-accent/80 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Con
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Premium Offer */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Premium Offer *
              </h2>
              
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Price ($) *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={premiumOffer.price}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          setPremiumOffer(prev => ({ ...prev, price: value }));
                        }
                      }}
                      className="w-full input-field"
                      placeholder="200"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Delivery Time *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={premiumOffer.deliveryValue}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow positive integers
                        if (value === '' || (Number.isInteger(Number(value)) && Number(value) >= 1)) {
                          setPremiumOffer(prev => ({ ...prev, deliveryValue: value }));
                        }
                      }}
                      className="w-full input-field"
                      placeholder="1"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Unit *
                    </label>
                    <select
                      value={premiumOffer.deliveryUnit}
                      onChange={(e) => setPremiumOffer(prev => ({ ...prev, deliveryUnit: e.target.value }))}
                      className="w-full input-field"
                      required
                    >
                      {deliveryUnits.map(unit => (
                        <option key={unit} value={unit}>
                          {unit.charAt(0).toUpperCase() + unit.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Pros
                    </label>
                    <div className="space-y-2">
                      {premiumOffer.pros.map((pro, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={pro}
                            onChange={(e) => updatePro('premium', index, e.target.value)}
                            className="flex-1 input-field"
                            placeholder="Enter a pro..."
                          />
                          {premiumOffer.pros.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removePro('premium', index)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addPro('premium')}
                        className="flex items-center text-accent hover:text-accent/80 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Pro
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Cons
                    </label>
                    <div className="space-y-2">
                      {premiumOffer.cons.map((con, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={con}
                            onChange={(e) => updateCon('premium', index, e.target.value)}
                            className="flex-1 input-field"
                            placeholder="Enter a con..."
                          />
                          {premiumOffer.cons.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCon('premium', index)}
                              className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addCon('premium')}
                        className="flex items-center text-accent hover:text-accent/80 text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Con
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* What's Included */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                What's Included
              </h2>
              
              <div className="space-y-3">
                {includes.map((inc, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={inc}
                      onChange={(e) => updateInclude(index, e.target.value)}
                      className="flex-1 input-field"
                      placeholder="Enter what's included..."
                    />
                    {includes.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeInclude(index)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addInclude}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Item
                </button>
              </div>
            </div>

            {/* Skills */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Skills & Expertise
              </h2>
              
              <div className="space-y-3">
                {skills.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={skill}
                      onChange={(e) => updateSkill(index, e.target.value)}
                      className="flex-1 input-field"
                      placeholder="Enter a skill..."
                    />
                    {skills.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                        className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addSkill}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Skill
                </button>
              </div>
            </div>

            {/* Portfolio */}
            <div className="card p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Portfolio Examples
              </h2>
              
              <div className="space-y-4">
                {portfolio.map((item, index) => (
                  <div key={index} className="border rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => updatePortfolioItem(index, 'description', e.target.value)}
                          className="w-full input-field"
                          placeholder="Describe this work..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Portfolio Image
                        </label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => handlePortfolioImageChange(index, e)}
                          className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-accent file:text-white hover:file:bg-accent-dark"
                        />
                        {item.imagePreview && (
                          <div className="mt-2">
                            <img 
                              src={item.imagePreview} 
                              alt="Portfolio preview" 
                              className="w-full max-w-md h-32 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Video URL (Optional)
                      </label>
                      <input
                        type="url"
                        value={item.videoUrl}
                        onChange={(e) => updatePortfolioItem(index, 'videoUrl', e.target.value)}
                        className="w-full input-field"
                        placeholder="https://youtube.com/watch?v=..."
                      />
                    </div>
                    {portfolio.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removePortfolioItem(index)}
                        className="mt-2 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addPortfolioItem}
                  className="flex items-center text-accent hover:text-accent/80"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Portfolio Item
                </button>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <PostQualityScore
              postType="gig"
              title={watch('title')}
              description={watch('description')}
              category={watch('category') || selectedCategory}
              subCategory={watch('subCategory') || selectedSubCategory}
              type={watch('type')}
              skills={skills}
              includes={includes}
              onScoreChange={(data) => setQualityPasses(Boolean(data?.passes))}
            />

            {/* Tips */}
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Tips for Success
              </h3>
              
              <div className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <p>Use clear, descriptive titles that highlight your value</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <p>Include high-quality portfolio examples</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <p>Set competitive but fair pricing</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-accent rounded-full mt-2 flex-shrink-0"></div>
                  <p>Be specific about what's included</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-8 flex flex-col items-end space-y-2">
          {!qualityPasses && (
            <p className="text-sm font-medium text-red-600 dark:text-red-400">
              Score must be above 80% to post this gig
            </p>
          )}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-outline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createGigMutation.isLoading || !qualityPasses}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createGigMutation.isLoading ? 'Creating...' : 'Create Gig'}
            </button>
          </div>
        </div>
      </form>

      <AiWriteModal
        isOpen={showAiModal}
        onClose={() => setShowAiModal(false)}
        mode="description"
        postType="gig"
        defaultTitle={watch('title') || ''}
        category={watch('category') || selectedCategory}
        type={watch('type')}
        onAccept={({ html, title }) => {
          if (title) {
            setValue('title', title, { shouldValidate: true, shouldDirty: true });
          }
          setValue('description', html || '', { shouldValidate: true, shouldDirty: true });
          setDescriptionEditorKey((k) => k + 1);
          toast.success('Description filled from AI — you can edit it below');
        }}
      />
    </div>
  );
};

export default CreateGigPage;